import { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { toast } from 'sonner';
import { RefreshCw, TrendingUp, TrendingDown, DollarSign, BarChart3, PieChart } from 'lucide-react';

const MONTHS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
  { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
  { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }
];

export default function FinanceReports() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [reportType, setReportType] = useState('monthly');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [yearlyReport, setYearlyReport] = useState(null);

  useEffect(() => {
    if (reportType === 'monthly') {
      fetchMonthlyReport();
    } else {
      fetchYearlyReport();
    }
  }, [reportType, selectedYear, selectedMonth]);

  const fetchMonthlyReport = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/finance/reports/monthly?year=${selectedYear}&month=${selectedMonth}`);
      setMonthlyReport(res.data);
    } catch (error) {
      console.error('Failed to fetch monthly report:', error);
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const fetchYearlyReport = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/finance/reports/yearly?year=${selectedYear}`);
      setYearlyReport(res.data);
    } catch (error) {
      console.error('Failed to fetch yearly report:', error);
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  const getCategoryLabel = (cat) => cat.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  
  const getCategoryColor = (index) => {
    const colors = [
      'bg-blue-500', 'bg-emerald-500', 'bg-amber-500', 'bg-purple-500', 
      'bg-red-500', 'bg-cyan-500', 'bg-orange-500', 'bg-pink-500',
      'bg-indigo-500', 'bg-teal-500', 'bg-lime-500', 'bg-rose-500'
    ];
    return colors[index % colors.length];
  };

  return (
    <div className="space-y-6" data-testid="finance-reports">
      {/* Report Controls */}
      <div className="flex flex-wrap gap-4 items-center">
        <Select value={reportType} onValueChange={setReportType}>
          <SelectTrigger className="w-[150px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="monthly">Monthly Report</SelectItem>
            <SelectItem value="yearly">Yearly Report</SelectItem>
          </SelectContent>
        </Select>

        <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
          <SelectTrigger className="w-[100px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {years.map(y => (
              <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {reportType === 'monthly' && (
          <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
            <SelectTrigger className="w-[130px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MONTHS.map(m => (
                <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <Button 
          variant="outline" 
          size="sm"
          onClick={() => reportType === 'monthly' ? fetchMonthlyReport() : fetchYearlyReport()}
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      ) : reportType === 'monthly' && monthlyReport ? (
        <>
          {/* Monthly Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="border-emerald-200 bg-emerald-50">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Total Income</p>
                    <p className="text-2xl font-bold text-emerald-600">
                      ${monthlyReport.total_income.toLocaleString()}
                    </p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-emerald-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-red-200 bg-red-50">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Total Expenses</p>
                    <p className="text-2xl font-bold text-red-600">
                      ${monthlyReport.total_expenses.toLocaleString()}
                    </p>
                  </div>
                  <TrendingDown className="h-8 w-8 text-red-400" />
                </div>
              </CardContent>
            </Card>

            <Card className={monthlyReport.net_profit >= 0 ? 'border-blue-200 bg-blue-50' : 'border-amber-200 bg-amber-50'}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Net Profit/Loss</p>
                    <p className={`text-2xl font-bold ${monthlyReport.net_profit >= 0 ? 'text-blue-600' : 'text-amber-600'}`}>
                      ${monthlyReport.net_profit.toLocaleString()}
                    </p>
                  </div>
                  <DollarSign className={`h-8 w-8 ${monthlyReport.net_profit >= 0 ? 'text-blue-400' : 'text-amber-400'}`} />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Transactions</p>
                    <p className="text-2xl font-bold text-slate-900">
                      {monthlyReport.transaction_count}
                    </p>
                  </div>
                  <BarChart3 className="h-8 w-8 text-slate-400" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Expense Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PieChart className="h-5 w-5" />
                Expense Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              {Object.keys(monthlyReport.expenses_by_category).length === 0 ? (
                <p className="text-slate-500 text-center py-8">No expenses recorded for this period</p>
              ) : (
                <div className="space-y-4">
                  {Object.entries(monthlyReport.expenses_by_category)
                    .sort((a, b) => b[1].total - a[1].total)
                    .map(([category, data], index) => (
                      <div key={category} className="space-y-2">
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full ${getCategoryColor(index)}`} />
                            <span className="font-medium">{getCategoryLabel(category)}</span>
                          </div>
                          <div className="text-right">
                            <span className="font-bold">${data.total.toLocaleString()}</span>
                            <span className="text-slate-500 ml-2">({data.percentage}%)</span>
                          </div>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${getCategoryColor(index)}`}
                            style={{ width: `${data.percentage}%` }}
                          />
                        </div>
                        <p className="text-xs text-slate-500">{data.count} transaction(s)</p>
                      </div>
                    ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      ) : reportType === 'yearly' && yearlyReport ? (
        <>
          {/* Yearly Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-emerald-200 bg-emerald-50">
              <CardContent className="pt-6">
                <p className="text-xs text-slate-500 uppercase">Year Total Income</p>
                <p className="text-3xl font-bold text-emerald-600">
                  ${yearlyReport.total_income.toLocaleString()}
                </p>
              </CardContent>
            </Card>

            <Card className="border-red-200 bg-red-50">
              <CardContent className="pt-6">
                <p className="text-xs text-slate-500 uppercase">Year Total Expenses</p>
                <p className="text-3xl font-bold text-red-600">
                  ${yearlyReport.total_expenses.toLocaleString()}
                </p>
              </CardContent>
            </Card>

            <Card className={yearlyReport.net_profit >= 0 ? 'border-blue-200 bg-blue-50' : 'border-amber-200 bg-amber-50'}>
              <CardContent className="pt-6">
                <p className="text-xs text-slate-500 uppercase">Year Net Profit/Loss</p>
                <p className={`text-3xl font-bold ${yearlyReport.net_profit >= 0 ? 'text-blue-600' : 'text-amber-600'}`}>
                  ${yearlyReport.net_profit.toLocaleString()}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Monthly Breakdown Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Monthly Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {MONTHS.map((month) => {
                  const data = yearlyReport.monthly_breakdown[month.value] || { income: 0, expenses: 0, net: 0 };
                  const maxVal = Math.max(
                    ...Object.values(yearlyReport.monthly_breakdown).map(d => Math.max(d.income, d.expenses))
                  ) || 1;
                  
                  return (
                    <div key={month.value} className="flex items-center gap-4">
                      <span className="w-20 text-sm text-slate-600">{month.label.slice(0, 3)}</span>
                      <div className="flex-1 flex gap-2">
                        <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden">
                          <div 
                            className="h-full bg-emerald-500 rounded-full"
                            style={{ width: `${(data.income / maxVal) * 100}%` }}
                            title={`Income: $${data.income.toLocaleString()}`}
                          />
                        </div>
                        <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden">
                          <div 
                            className="h-full bg-red-500 rounded-full"
                            style={{ width: `${(data.expenses / maxVal) * 100}%` }}
                            title={`Expenses: $${data.expenses.toLocaleString()}`}
                          />
                        </div>
                      </div>
                      <span className={`w-24 text-right text-sm font-medium ${data.net >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        ${data.net.toLocaleString()}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-6 mt-4 pt-4 border-t justify-center">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-emerald-500" />
                  <span className="text-sm text-slate-600">Income</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <span className="text-sm text-slate-600">Expenses</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Category Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>Category Totals</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(yearlyReport.category_breakdown)
                  .sort((a, b) => b[1] - a[1])
                  .map(([category, total], index) => (
                    <div key={category} className="p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <div className={`w-2 h-2 rounded-full ${getCategoryColor(index)}`} />
                        <span className="text-sm text-slate-600">{getCategoryLabel(category)}</span>
                      </div>
                      <p className="text-lg font-bold text-slate-900">${total.toLocaleString()}</p>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <div className="text-center py-12">
          <BarChart3 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No data available for this period</p>
        </div>
      )}
    </div>
  );
}
